import { gql } from "@/gql/__generated__";
import {
  AppDeploymentHeartbeatMutation,
  AppDeploymentStatus,
  SharedAppDeploymentHeartbeatMutation,
  SubscriptionAppDeploymentHeartbeatMutation
} from "@/gql/__generated__/graphql";
import { ApolloError, MutationResult, useMutation } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

const APP_DEPLOYMENT_HEARTBEAT = gql(`
    mutation appDeploymentHeartbeat($deploymentId: ID!) {
        appDeployHeartbeat(deployID: $deploymentId){
            __typename
            status
            proxyURL
        }
    }
`);

const SUBSCRIPTION_APP_DEPLOYMENT_HEARTBEAT = gql(`
  mutation subscriptionAppDeploymentHeartbeat($deploymentId: ID!) {
    subscriptionDeployHeartbeat(subscriptionId: $deploymentId) {
      __typename
      status
      proxyURL
    }
  }
`);

const SHARED_APP_DEPLOYMENT_HEARTBEAT = gql(`
  mutation sharedAppDeploymentHeartbeat($deploymentId: ID!) {
    appSharedDeployHeartbeat(sharedURLID: $deploymentId) {
      __typename
      status
      proxyURL
    }
  }
`);

export enum DeploymentType {
  Organization,
  Subscription,
  Shared
}

const deploymentHeartbeatMap = {
  [DeploymentType.Organization]: APP_DEPLOYMENT_HEARTBEAT,
  [DeploymentType.Subscription]: SUBSCRIPTION_APP_DEPLOYMENT_HEARTBEAT,
  [DeploymentType.Shared]: SHARED_APP_DEPLOYMENT_HEARTBEAT
} as const;

interface Props {
  deploymentId: string;
  deploymentType: DeploymentType;
}

interface HeartbeatProps {
  id: string;
  analyticsDisabled: boolean;
  controller: AbortController;
}

type DeploymentHeartbeatReturn = Omit<
  MutationResult<
    | AppDeploymentHeartbeatMutation
    | SubscriptionAppDeploymentHeartbeatMutation
    | SharedAppDeploymentHeartbeatMutation
  >,
  "data"
> & {
  data:
    | AppDeploymentHeartbeatMutation["appDeployHeartbeat"]
    | SubscriptionAppDeploymentHeartbeatMutation["subscriptionDeployHeartbeat"]
    | SharedAppDeploymentHeartbeatMutation["appSharedDeployHeartbeat"]
    | null
    | undefined;
  networkError: boolean;
};

export function useDeploymentHeartbeat({
  deploymentId,
  deploymentType
}: Props): DeploymentHeartbeatReturn {
  const [deploymentStatus, setDeploymentStatus] =
    useState<AppDeploymentStatus>();
  const [networkError, setNetworkError] = useState(false);

  const [mutation, results] = useMutation<
    | AppDeploymentHeartbeatMutation
    | SubscriptionAppDeploymentHeartbeatMutation
    | SharedAppDeploymentHeartbeatMutation
  >(deploymentHeartbeatMap[deploymentType]);

  const heartbeat = useCallback(
    ({ id, analyticsDisabled, controller }: HeartbeatProps) => {
      mutation({
        variables: { deploymentId: id },
        context: {
          headers: { AnalyticsDoNotTrack: analyticsDisabled },
          fetchOptions: { signal: controller.signal }
        }
      })
        .then((res) => {
          setNetworkError(false);
          if (!res.data) return;
          let status;

          if ("appDeployHeartbeat" in res.data)
            status = res.data.appDeployHeartbeat.status;
          else if ("subscriptionDeployHeartbeat" in res.data)
            status = res.data.subscriptionDeployHeartbeat.status;
          else if ("appSharedDeployHeartbeat" in res.data)
            status = res.data.appSharedDeployHeartbeat.status;

          setDeploymentStatus(status);
        })
        .catch((err) => {
          // If we did not force this error by aborting the mutation, print error in console
          if (!controller.signal.aborted) {
            setNetworkError(!!(err as ApolloError).networkError);
            console.error(err);
          }
        });
    },
    [mutation]
  );

  // initial heartbeat
  useEffect(() => {
    if (!deploymentId || deploymentStatus) return undefined;

    const controller = new AbortController();
    heartbeat({ id: deploymentId, analyticsDisabled: false, controller });

    return () => controller.abort();
  }, [heartbeat, deploymentStatus, deploymentId]);

  // Send out heartbeat at polling rate determined by the deployments current status
  useEffect(() => {
    if (
      !deploymentId ||
      deploymentStatus === undefined ||
      deploymentStatus === AppDeploymentStatus.Error
    )
      return undefined;

    let controller: AbortController;
    const interval = setInterval(
      () => {
        const c = new AbortController();
        controller = c;
        heartbeat({ id: deploymentId, analyticsDisabled: true, controller: c });
      },
      deploymentStatusToPollingRate(networkError, deploymentStatus)
    );

    return () => {
      clearInterval(interval);
      if (controller) controller.abort();
    };
  }, [deploymentStatus, heartbeat, deploymentId, networkError]);

  if (!results.data) return { ...results, data: null, networkError };

  let data: DeploymentHeartbeatReturn["data"];

  if ("appDeployHeartbeat" in results.data)
    data = results.data.appDeployHeartbeat;
  else if ("subscriptionDeployHeartbeat" in results.data)
    data = results.data.subscriptionDeployHeartbeat;
  else if ("appSharedDeployHeartbeat" in results.data)
    data = results.data.appSharedDeployHeartbeat;

  return { ...results, data, networkError };
}

function deploymentStatusToPollingRate(
  networkError: boolean,
  status: AppDeploymentStatus = AppDeploymentStatus.Unknown
) {
  if (networkError) return 5_000; // 5 seconds}

  switch (status) {
    case AppDeploymentStatus.Running:
      return 60_000; // 1 minute
    case AppDeploymentStatus.Error:
      return 0; // Stop
    case AppDeploymentStatus.Pending:
    case AppDeploymentStatus.Stopped:
    case AppDeploymentStatus.Unknown:
      return 5_000; // 5 seconds
    default:
      return 5_000; // 5 seconds
  }
}
